define((require) => {
    "use strict";

    const Configuration             = require("shared/services/Configuration");
    const EditorEvents              = require("shared/services/EditorEvents");
    const ImprovedView              = require("editor/zbone/ImprovedView");
    const NotificationBar           = require("editor/ui/notificationbar/NotificationBar");
    const Permissions               = require("shared/security/Permissions");
    const Promise                   = require("shared/util/Promise");
    const {isBlank}                 = require("shared/util/StringUtils");
    const SyncManager               = require("shared/services/sync/SyncManager");
    const SequentialButton          = require("util/SequentialButton");
    const Tooltip                   = require("ui/common/Tooltip");
    const TagInputView              = require("components/tag_input/TagInputView");
    const template                  = require("ui/experience/widgets/salesforceCommerceWidget/settings/view/SalesforceCommerceWidgetSettingsView.tpl");
    const ValidateBehavior          = require("ui/behaviors/ValidateBehavior");
    const SalesforceCommerceConnect = require("ui/experience/widgets/salesforceCommerceWidget/service/SalesforceCommerceConnect");

    const ARTICLE_LINK = `${Configuration.communityBaseUrl}/hc/en-us/articles/204729309-SalesforceCommerce-Product-Link-Widget`;
    const UPDATED_MESSAGE = "Your Salesforce Commerce Cloud settings have been saved.";
    const FAILED_MESSAGE = "Your Salesforce Commerce Cloud settings have not been saved.";
    const CONNECT_ERROR_MESSAGE = `But failed to connect to the Salesforce Commerce Cloud Server. Please check your setup - for more information, see the article of <a class='widget-community-link' href='${ARTICLE_LINK}' target='zmags_creator_community'>Salesforce Commerce Cloud Widget</a>.`;

    /**
     * The salesforceCommerce widget settings view class that enables the user to Update the settings
     * of the widget.
     *
     * @class SalesforceCommerceWidgetSettingsView
     *
     * @cfg {CompanySettingsModel} model.
     * @author Pedro Augusto / <a href="http://www.zmags.com">Zmags</a>
     */
    return ImprovedView.extend({

        template,

        // SalesforceCommerce settings model, @see {SalesforceCommerceSettingsModel}
        salesforceCommerceSettings: null,

        // Notification message is shown when settings are saved
        notificationMessage: "",

        // Postfix notification in case of any errors when saving to give additional info
        postfixError: "",

        ui: {
            clientId: ".salesforceCommerceClientId",
            userUrl: ".salesforceCommerceUrl",
            save: ".save"
        },

        regions: {
            "sites": ".site-names"
        },

        templateHelpers() {
            return {
                isSalesforceCommerceEnabled:    SalesforceCommerceConnect.isQuickViewEnabled(),
                permissionToManage:             Permissions.Privileges.ADMIN.can(),
                articleLink:                    ARTICLE_LINK
            };
        },

        events: {
            "click .save": "_saveSettingsHandler"
        },

        behaviors: {
            validateBehavior: {
                behaviorClass: ValidateBehavior,
                isEnabled() {
                    return Permissions.Privileges.ADMIN.can();
                },
                inputValidation: {
                    ".salesforceCommerceUrl": {
                        validateAfter: "beforeSubmit",
                        // mark as bound to model, so make sure we cancel pending save requests on error!
                        model: true,
                        validator(userUrl) {
                            // It is need to check the data in the clientId in case the user want to remove the salesforceCommerce
                            // credentials.
                            return (isBlank(userUrl) && isBlank(this.ui.clientId.val())) || SalesforceCommerceConnect.validateUrl(userUrl) === null;
                        },
                        errorText(userUrl) {
                            return SalesforceCommerceConnect.validateUrl(userUrl);
                        }
                    },
                    ".salesforceCommerceClientId": {
                        validateAfter: "beforeSubmit",
                        // mark as bound to model, so make sure we cancel pending save requests on error!
                        model: true,
                        validator(key) {
                            return (isBlank(key) && isBlank(this.ui.userUrl.val())) || SalesforceCommerceConnect.validateClientId(key.trim());
                        },
                        errorText() {
                            return "Not a valid Salesforce Commerce Cloud Client ID";
                        }
                    }
                }
            }
        },

        salesforceCommerceBindings: {
            ".salesforceCommerceUrl": {
                observe: "userUrl",
                onSet(val) {
                    return val.trim();
                }
            },
            // We don't need to trim because the format is guarded by validation which does not allow empty spaces.
            ".salesforceCommerceClientId": {
                observe: "clientId",
                onSet(val) {
                    return val.trim();
                }
            }
        },

        filterBindings: {
            ".name":  "name",
            ".price": "price",
            ".brand": "brand",
            ".description":  "description",
            ".availability": "availability"
        },

        initialize() {
            this.salesforceCommerceSettings = SalesforceCommerceConnect.getSettings();

            // Clone a collection for editing and we copy them back once save button is hit
            this.clonedSites = this.salesforceCommerceSettings.get("sites").deepClone();
            this.oldCredentail = {
                userURL: this.salesforceCommerceSettings.get("userUrl"),
                clientId: this.salesforceCommerceSettings.get("clientId")
            };
        },

        onRender() {
            // Bind model with the custom salesforceCommerceWebApiSettings
            this.stickit(this.salesforceCommerceSettings, this.salesforceCommerceBindings);

            // Bind model with the custom salesforceCommerceFilterSettings
            this.stickit(this.salesforceCommerceSettings.get("filters"), this.filterBindings);
        },

        onShow() {
            this._saveSettingsHandler = SequentialButton.add(this.ui.save, this._saveSettingsHandler.bind(this));

            const tagsView = new TagInputView({
                collection: this.clonedSites,
                propName: "siteId"
            });

            this.sites.show(tagsView);
            SyncManager.onSync({
                context:  this,
                target:   this.model,
                // Re-render because we need to disable the delete button
                callback: tagsView.render.bind(tagsView)
            });

            if (this.salesforceCommerceSettings.get("enabled")) {
                this.listenTo(this.model, "change:demandware", this._settingChangeHandler);
                this.listenTo(this.clonedSites, "add", this.enableSaveButton);
                this.listenTo(this.salesforceCommerceSettings.get("filters"), "change", this.enableSaveButton);
                this.configureTooltip();
            }
        },

        _settingChangeHandler() {
            this.enableSaveButton();
            this.configureTooltip();
        },

        enableSaveButton() {
            if (Permissions.Privileges.ADMIN.can()) {
                this.ui.save.prop("disabled", false);
            }
        },

        disableSaveButton() {
            this.ui.save.prop("disabled", true);
        },

        /**
         * Adds a tooltip to show the fully qualified url for the SalesforceCommerce API when available.
         *
         *  @private
         */
        configureTooltip() {
            const webApiUrl = this.salesforceCommerceSettings.get("webApiUrl");

            if (webApiUrl) {
                Tooltip.add(this.ui.userUrl, {
                    content:   `Default inferred Salesforce Commerce Cloud URL: <b>${webApiUrl}</b>`,
                    placement: "top",
                    duration:  10000,
                    delay:     {show: 1500, hide: 0},
                    width: 350
                });
            } else {
                Tooltip.remove(this.ui.userUrl);
            }
        },

        /**
         * Here we update the settings. We first check if we can connect to the SalesforceCommerce default site. If that works,
         * we then validate all the site ids and save the settings. Otherwise, we skip the validation
         * but save the settings. In the end, we clean up by calling internal finish.
         *
         * @private
         */
        _saveSettingsHandler() {
            const settings = this.salesforceCommerceSettings;
            const clientId = settings.get("clientId");
            const userUrl = settings.get("userUrl");
            const sequentialButton = Promise.deferred();
            let promise = null;

            if (SalesforceCommerceConnect.validateUrl(userUrl) || !SalesforceCommerceConnect.validateClientId(clientId)) {
                promise = Promise.rejected(null);
            } else if (clientId === this.oldCredentail.clientId && userUrl === this.oldCredentail.userURL) {
                // If the credentials are the same, then the inferred URL will be the same as well.
                promise = Promise.resolved(settings.get("webApiUrl"));
            } else {
                promise = SalesforceCommerceConnect.inferAddress(userUrl, clientId);
            }

            // Finishing up after everything is saved, including stop spinning, disable button and showing notification
            const finish = () => {
                let method = "open";

                sequentialButton.resolve();
                this.disableSaveButton();

                if (this.postfixError) {
                    method = "error";
                }

                let siteList = [];

                if (this.clonedSites) {
                    siteList = this.clonedSites.pluck("siteId");
                }

                const eventProperties = {
                    category: "Commerce",
                    salesforceCommerceUrl: userUrl,
                    salesforceCommerceSites: siteList.join() || "No Site",
                    integrationType: "SalesforceCommerce",
                    triggerOrigin: "SaveSalesforceCommerceSettingsButton"
                };

                EditorEvents.trigger(EditorEvents.constants.SAVE_INTEGRATION, eventProperties);

                NotificationBar[method](this.notificationMessage + " " + this.postfixError, {closeDelay: 7000});

                // Reset the error message otherwise it will keep showing in the subsequent saving
                this.postfixError = "";
            };

            /*
             * Note: we may be attempted to return this promise. But instead, we return the sequentialButton promise
             * below because we need to wait for all the promises to finish in both then and fail cases. So the
             * sequential button works in a good manner, otherwise the spinning may stop while requests are going on.
             */
            promise
                .then((url) => {
                    // Validate all site ids as well as save the setting to backend, followed by a finish up.
                    // Note: don't use Promise.all here, because we need to wait until both validation and save are done,
                    // regardless resolved or rejected. With Promise.all, it will be resolved once any promise is rejected
                    Promise.any([this._validateSiteIds(), this._save(url)]).then(finish);
                })
                .fail(() => {
                    this.postfixError = CONNECT_ERROR_MESSAGE;
                    // We still want to save the settings even though it fails
                    this._save().always(finish);
                });

            return sequentialButton;
        },

        /**
         * Save the current settings to the backend
         *
         * @param inferredURL   The inferred url queried based on the clientId and url.
         * @returns {Promise}
         * @private
         */
        _save(inferredURL) {
            const settings = this.salesforceCommerceSettings;

            // Update the old credential, so we can compare again. Note it is worth keeping the reference
            // because finding an inferred url is expensive.
            this.oldCredentail = {
                userURL: settings.get("userUrl"),
                clientId: settings.get("clientId")
            };

            settings.set({
                webApiUrl: inferredURL,
                sites: this.clonedSites
            });

            return this.model.saveLater()
                .done(() => {
                    this.notificationMessage = UPDATED_MESSAGE;
                }).fail(() => {
                    this.notificationMessage = FAILED_MESSAGE;
                });
        },

        /**
         * Validate all site ids and return a promise which is resolved when all validation is done
         *
         * @returns {Promise} promise
         * @private
         */
        _validateSiteIds() {
            const settings = this.salesforceCommerceSettings;
            const failed = [];

            const promises = this.clonedSites.map((site) => {
                const siteId = site.get("siteId");

                return SalesforceCommerceConnect.inferAddress(settings.get("userUrl"), settings.get("clientId"), siteId)
                    .fail(() => {
                        failed.push(siteId);
                    });
            });

            return Promise.any(promises).then(() => {
                if (failed.length) {
                    this.postfixError = "But failed to connect to " + failed.join(", ");
                }
            });
        },

        beforeNavigate() {
            return this.ui.save.length > 0 && !this.ui.save.prop("disabled");
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/experience/widgets/salesforceCommerceWidget/settings/view/SalesforceCommerceWidgetSettingsView.js